import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Search, Home, Bell, Moon, Sun, Network,
  Settings, LogOut, Hash, Globe2, Compass,
  TrendingUp, Bookmark, Menu, X, LucideIcon
} from 'lucide-react';
import { CATEGORIES } from '../types';
import * as Icons from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import ActionIcon from './common/ActionIcon';
import NotificationDropdown from './notifications/NotificationDropdown';
import { useNotifications } from '../hooks/useNotifications';
import groupIcon from '/images/group-icon.svg'

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showCategoriesDropdown, setShowCategoriesDropdown] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const categoriesRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = () => {
    logout();
  };

  const handleProfileClick = () => {
    if (user?.id) {
      navigate(`/profile/${user.id}`);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchFocused(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (categoriesRef.current && !categoriesRef.current.contains(event.target as Node)) {
        setShowCategoriesDropdown(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navigationItems = [
    { icon: <Home className="h-5 w-5" />, label: 'Home', path: '/' },
    { icon: <Globe2 className="h-5 w-5" />, label: 'Regional', path: '/regional' },
    { icon: <TrendingUp className="h-5 w-5" />, label: 'Trending', path: '/trending' },
    { icon: <Bookmark className="h-5 w-5" />, label: 'Saved', path: '/bookmarks' },
    { icon: <Compass className="h-5 w-5" />, label: 'Explore', path: '/explore' }
  ];

  return (
    <header className={`
      sticky top-0 z-40 transition-colors duration-200
      ${theme === 'dark' ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}
      border-b
    `}>
      <div className="max-w-7xl mx-auto">
        <div className="h-14 flex items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Left: Logo and Mobile Menu */}
          <div className="flex items-center space-x-4">
            <Link to="/" className="flex items-center">
              <img 
                src={groupIcon} 
                alt="Group Logo" 
                className="h-9 w-auto"
              />
            </Link>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-1.5 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <X className={`h-5 w-5 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`} />
              ) : (
                <Menu className={`h-5 w-5 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`} />
              )}
            </button>
          </div>

          {/* Center: Search (Hidden on Mobile) */}
          <div className="hidden md:block flex-1 max-w-xl px-4">
            <div className="relative" ref={searchRef}>
              <input
                type="text"
                placeholder="Search..."
                className={`
                  w-full px-4 py-1.5 pl-9 rounded-lg transition-all duration-200 text-sm
                  ${theme === 'dark'
                    ? 'bg-gray-800 text-gray-100 placeholder-gray-400 focus:bg-gray-700'
                    : 'bg-gray-100 text-gray-900 placeholder-gray-500 focus:bg-white'
                  }
                  border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                `}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
              />
              <Search className={`absolute left-3 top-2 h-4 w-4 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`} />
            </div>
          </div>

          {/* Right: Navigation and Actions */}
          <div className="flex items-center space-x-1">
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navigationItems.map(item => (
                <ActionIcon
                  key={item.path}
                  icon={item.icon}
                  label={item.label}
                  onClick={() => navigate(item.path)}
                  active={isActive(item.path)}
                />
              ))}

              {/* Categories Dropdown */}
              <div ref={categoriesRef} className="relative">
                <ActionIcon
                  icon={<Hash className="h-5 w-5" />}
                  label="Categories"
                  onClick={() => setShowCategoriesDropdown(!showCategoriesDropdown)}
                  active={showCategoriesDropdown}
                />

                {showCategoriesDropdown && (
                  <div className={`
                    absolute right-0 mt-2 w-64 rounded-lg shadow-lg py-2
                    ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
                  `}>
                    {CATEGORIES.map(category => {
                      const CategoryIcon = Icons[category.icon as keyof typeof Icons] as LucideIcon;
                      return (
                        <button
                          key={category.id}
                          onClick={() => {
                            navigate(`/category/${category.slug}`);
                            setShowCategoriesDropdown(false);
                          }}
                          className={`
                            w-full px-4 py-2 flex items-center space-x-3
                            transition-colors
                            ${theme === 'dark'
                              ? 'hover:bg-gray-700 text-gray-300'
                              : 'hover:bg-gray-100 text-gray-700'
                            }
                          `}
                        >
                          {CategoryIcon && <CategoryIcon className="h-5 w-5 text-blue-500" />}
                          <span>{category.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Theme Toggle */}
            <ActionIcon
              icon={theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
              label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              onClick={toggleTheme}
            />

            {/* Notifications */}
            <div ref={notificationsRef} className="relative">
              <div className="relative">
                <ActionIcon
                  icon={<Bell className="h-5 w-5" />}
                  label="Notifications"
                  onClick={() => setShowNotifications(!showNotifications)}
                  active={showNotifications}
                />
                {unreadCount > 0 && (
                  <div className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 flex items-center justify-center">
                    <span className="text-[10px] font-medium text-white">
                      {unreadCount}
                    </span>
                  </div>
                )}
              </div>

              {showNotifications && (
                <NotificationDropdown
                  notifications={notifications}
                  onClose={() => setShowNotifications(false)}
                  onMarkAsRead={markAsRead}
                  onMarkAllAsRead={markAllAsRead}
                />
              )}
            </div>

            {/* User Menu */}
            <div className="relative group">
              <button 
                className="flex items-center"
                onClick={handleProfileClick}
              >
                <img
                  src={user?.avatar}
                  alt="Profile"
                  className="h-8 w-8 rounded-full ring-2 ring-blue-500/20"
                />
              </button>

              <div className={`
                absolute right-0 mt-2 w-48 rounded-lg shadow-lg py-1
                transition-all duration-200 origin-top-right
                invisible group-hover:visible opacity-0 group-hover:opacity-100
                ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
              `}>
                {/* <button
                  onClick={handleProfileClick}
                  className={`
                    w-full px-4 py-2 text-sm flex items-center space-x-2
                    transition-colors
                    ${theme === 'dark'
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  <span>Profile</span>
                </button> */}
                <button
                  onClick={() => navigate('/settings')}
                  className={`
                    w-full px-4 py-2 text-sm flex items-center space-x-2
                    transition-colors
                    ${theme === 'dark'
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  <Settings className="h-4 w-4" />
                  <span>Settings</span>
                </button>
                <button
                  onClick={handleLogout}
                  className={`
                    w-full px-4 py-2 text-sm flex items-center space-x-2
                    transition-colors
                    ${theme === 'dark'
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Search Bar */}
        <div className={`
          md:hidden px-4 pb-3 transition-all duration-200
          ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}>
          <div className="relative">
            <input
              type="text"
              placeholder="Search..."
              className={`
                w-full px-4 py-2 pl-9 rounded-lg transition-all duration-200 text-sm
                ${theme === 'dark'
                  ? 'bg-gray-800 text-gray-100 placeholder-gray-400'
                  : 'bg-gray-100 text-gray-900 placeholder-gray-500'
                }
                border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}
                focus:outline-none focus:ring-2 focus:ring-blue-500
              `}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Search className={`absolute left-3 top-2.5 h-4 w-4 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`} />
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        ref={mobileMenuRef}
        className={`
          fixed inset-0 z-50 lg:hidden transition-all duration-300
          ${isMobileMenuOpen ? 'visible' : 'invisible'}
        `}
      >
        <div 
          className={`
            absolute inset-0 bg-black/50 transition-opacity duration-300
            ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0'}
          `}
          onClick={() => setIsMobileMenuOpen(false)}
        />

        <div className={`
          absolute right-0 top-0 h-full w-[80%] max-w-sm
          transform transition-transform duration-300 ease-out
          ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}
          ${theme === 'dark'
            ? 'bg-gray-900'
            : 'bg-white'
          }
        `}>
          <div className="flex flex-col h-full">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <button
                  onClick={handleProfileClick}
                  className="flex items-center space-x-3"
                >
                  <img
                    src={user?.avatar}
                    alt={user?.username}
                    className="h-10 w-10 rounded-full ring-2 ring-blue-500/20"
                  />
                  <div>
                    <p className={`font-medium ${
                      theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                    }`}>
                      @{user?.username}
                    </p>
                    <p className={`text-sm ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {user?.role}
                    </p>
                  </div>
                </button>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`p-2 rounded-lg ${
                    theme === 'dark'
                      ? 'hover:bg-gray-800 text-gray-400'
                      : 'hover:bg-gray-100 text-gray-500'
                  }`}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto py-4 px-4">
              <div className="space-y-1">
                {navigationItems.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`
                      flex items-center space-x-3 px-4 py-3 rounded-lg
                      transition-colors
                      ${theme === 'dark'
                        ? 'text-gray-300 hover:bg-gray-800'
                        : 'text-gray-700 hover:bg-gray-100'
                      }
                      ${isActive(item.path)
                        ? theme === 'dark'
                          ? 'bg-gray-800 text-white'
                          : 'bg-gray-100 text-gray-900'
                        : ''
                      }
                    `}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </Link>
                ))}

                {/* Categories in Mobile Menu */}
                <div className="mt-4">
                  <div className={`px-4 py-2 text-sm font-medium ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Categories
                  </div>
                  {CATEGORIES.map(category => {
                    const CategoryIcon = Icons[category.icon as keyof typeof Icons] as LucideIcon;
                    return (
                      <Link
                        key={category.id}
                        to={`/category/${category.slug}`}
                        className={`
                          flex items-center space-x-3 px-4 py-3 rounded-lg
                          transition-colors
                          ${theme === 'dark'
                            ? 'text-gray-300 hover:bg-gray-800'
                            : 'text-gray-700 hover:bg-gray-100'
                          }
                        `}
                      >
                        {CategoryIcon && <CategoryIcon className="h-5 w-5 text-blue-500" />}
                        <span>{category.name}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleLogout}
                className={`
                  w-full flex items-center space-x-3 px-4 py-3 rounded-lg
                  transition-colors
                  ${theme === 'dark'
                    ? 'text-gray-300 hover:bg-gray-800'
                    : 'text-gray-700 hover:bg-gray-100'
                  }
                `}
              >
                <LogOut className="h-5 w-5" />
                <span>Sign out</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}